import React, { FC, useCallback, useEffect, useState } from 'react'

import { Collapse, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import { startOfDay } from 'date-fns'
import { isEqual, isNull } from 'lodash'

import { CommonTableColumnsEnum } from '../../../components/Table/types'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { SortDirection } from '../../../types'
import { useSort } from '../../../utils'
import { resetExpandAll, updateColumnOrder } from '../../DailyStats'
import { Trade } from '../../TradingLog/types'
import { DailyStatsProps } from '../types'

import { DailyStatsCardBody } from './DailyStatsCardBody'
import { DailyStatsCardHead } from './DailyStatsCardHead'

const DailyStatsCardComponent: FC<DailyStatsProps<Trade>> = ({ date, data, columns }) => {
  const { dailyStatsInfo } = data
  const { spacing, palette } = useTheme()
  const [opened, setOpened] = useState<boolean>(false)

  const { expandedDate, expandedAll } = useAppSelector((state) => state.dailyStats)

  const dispatch = useAppDispatch()

  const handleSetOpened = useCallback(() => {
    setOpened((state) => !state)
    dispatch(resetExpandAll())
  }, [setOpened])

  const dispatchNewReorder = (data: (keyof Trade)[]) => {
    dispatch(updateColumnOrder(data))
  }

  const { sortingColumn, handleSort, sortingDirection, sortedTrades, handleColumnReorder } =
    useSort<Trade>(data.tradingLogTableData, columns, dispatchNewReorder, {
      orderBy: CommonTableColumnsEnum.openTime,
      direction: SortDirection.desc,
    })

  useEffect(() => {
    if (!isNull(expandedAll)) {
      const expanded =
        expandedAll || isEqual(startOfDay(new Date(date)).toISOString(), expandedDate)
      setOpened(expanded)
    }
  }, [expandedAll, expandedDate])

  return (
    <Box
      p={spacing(3, 0)}
      bgcolor={palette.background.paper}
      borderRadius={spacing(0.5)}
      sx={{
        // width: '100%',
        // maxWidth: '100%',
        overflowX: 'auto',
      }}
    >
      <DailyStatsCardHead
        date={date}
        opened={opened}
        dailyStatsInfo={dailyStatsInfo}
        handleSetOpened={handleSetOpened}
      />

      <Collapse in={opened}>
        <DailyStatsCardBody
          sortingProps={{
            handleSort,
            handleColumnReorder,
            sortingColumn,
            sortedTrades,
            sortingDirection,
          }}
          keyForRefresh={date}
        />
      </Collapse>
    </Box>
  )
}

export const DailyStatsCard = React.memo(DailyStatsCardComponent, isEqual)
